import React, { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { closeModal, ModalType } from "store/features/modalSlice";

import featureAnnouncementService from "services/featureAnnouncements";

import Button from "components/Button";
import VideoModal from "components/VideoModal";

import { getYoutubeEmbedUrl } from "shared/functions";

import PlaceholderImage from "assets/images/placeholder-modal-img.jpg";
import { ReactComponent as PlayIcon } from "assets/images/play-icon.svg";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

const GlobalModal: React.FC = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);

  const dispatch = useAppDispatch();

  const isOpen = useAppSelector((state) => state.modal.isOpen);
  const modalType = useAppSelector((state) => state.modal.modalType);

  const {
    featureId,
    title = "Оновлення продукту",
    content = "",
    imageUrl = PlaceholderImage,
    videoUrl = "",
    videoUrlMobile = "",
  } = useAppSelector((state) => state.modal.modalProps);

  useEffect(() => {
    if (isOpen) {
      analytics().sendConversionEvent(
        AnalyticsEvents.feature_announcement_open,
        { featureId },
        true,
      );
    }
  }, [isOpen, featureId]);
  const currentVideoUrl =
    isMobile && videoUrlMobile ? videoUrlMobile : videoUrl;
  const fullVideoUrl = getYoutubeEmbedUrl(currentVideoUrl);

  const onPlayVideo = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.feature_announcement_video_play_img,
      { featureId },
      true,
    );
    setShowVideoModal(true);
    if (featureId)
      featureAnnouncementService().dismissFeatureAnnouncement(featureId);
    dispatch(closeModal());
  };

  const onCloseVideoModal = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.feature_announcement_video_close,
      { featureId },
      true,
    );
    setShowVideoModal(false);
  };

  const remindLater = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.feature_announcement_remind_later,
      { featureId },
      true,
    );
    if (featureId)
      featureAnnouncementService().postponeFeatureAnnouncement(featureId);
    dispatch(closeModal());
  };

  const tryNow = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.feature_announcement_video_play_btn,
      { featureId },
      true,
    );
    videoUrl && setShowVideoModal(true);
    if (featureId)
      featureAnnouncementService().dismissFeatureAnnouncement(featureId);
    dispatch(closeModal());
  };

  return (
    <>
      <Dialog
        open={isOpen}
        slotProps={{
          paper: { sx: { padding: "0 !important", borderRadius: "16px" } },
        }}
      >
        <DialogContent>
          {modalType === ModalType.VIDEO && videoUrl && (
            <Box sx={styles.thumbnailContainer} onClick={onPlayVideo}>
              <Box
                component="img"
                alt="modal-img"
                src={imageUrl}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <Box component={PlayIcon} sx={styles.playIcon} />
            </Box>
          )}
          {modalType === ModalType.INFO && (
            <Box sx={styles.imgContainer}>
              <Box
                component="img"
                alt="modal-img"
                src={imageUrl}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          {content && (
            <Typography sx={{ mb: 0.5 }} color="grey700">
              {content}
            </Typography>
          )}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={{ xs: "center", sm: "flex-end" }}
            spacing={2}
            sx={{ mt: 2 }}
          >
            <Button variant="contained" color="secondary" onClick={remindLater}>
              Нагадати пізніше
            </Button>
            <Button variant="contained" color="primary" onClick={tryNow}>
              Дивитися відео
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <VideoModal
        isModalOpen={showVideoModal}
        onClose={onCloseVideoModal}
        videoUrl={fullVideoUrl}
      />
    </>
  );
};

const styles = {
  imgContainer: {
    borderRadius: "20px",
    marginBottom: "24px",
    width: "100%",
  },
  thumbnailContainer: {
    position: "relative",
    cursor: "pointer",
    lineHeight: 0,
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "24px",
    width: "100%",
    minHeight: "150px",
  },
  playIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    width: { xs: "42px", sm: "60px" },
    height: { xs: "42px", sm: "60px" },
  },
};

export default GlobalModal;
